import { Capacitor } from '@capacitor/core';
// Side-effect only: onesignal-cordova-plugin's package.json declares "type":
// "module" but "main" points at a plain CJS file, and depending on how the
// bundler interops that mismatch, `import OneSignal from '...'` can end up
// bound to the wrong thing (the whole exports namespace instead of the
// actual plugin instance), silently breaking every call with "X is not a
// function". The module always attaches itself to window.plugins.OneSignal
// as a plain runtime global regardless of that ambiguity (the classic
// Cordova plugin pattern) - read it from there instead of trusting the
// import binding.
import 'onesignal-cordova-plugin';
import { supabase } from '../supabaseClient';

const OneSignal = () => window.plugins && window.plugins.OneSignal;

const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

const canUsePush = () => Capacitor.isNativePlatform() && !!ONESIGNAL_APP_ID;

// A short trail of what the push setup did on this device, shown on the profile
// page (native app only) so a phone that isn't receiving pushes can be
// diagnosed without a debugger attached.
const trail = [];
const note = (message) => {
  trail.push(`${new Date().toLocaleTimeString('en-GB')} ${message}`);
  if (trail.length > 40) trail.shift();
};

// The Cordova bridge (cordova.js) is what lets the OneSignal plugin talk to the
// native side. Capacitor only injects it into pages served from the APK's own
// bundled files; when the app loads the live site instead (capacitor.config
// server.url - how web updates reach phones without a new APK), nothing injects
// it, so load the copies the site serves at its root (public/cordova.js,
// cordova_plugins.js and plugins/ - copied from android/app/src/main/assets/public
// by scripts/sync-cordova.js) ourselves. Resolves once the bridge is usable, or
// after a timeout so a slow load can never hang the app.
const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.onload = resolve;
    el.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(el);
  });

let cordovaReady = null;
const ensureCordova = () => {
  if (window.cordova && window.cordova.exec) return Promise.resolve();
  if (!cordovaReady) {
    note('loading cordova.js');
    cordovaReady = loadScript('/cordova.js')
      .then(() => {
        note('cordova.js loaded');
        return loadScript('/cordova_plugins.js');
      })
      .then(() => {
        note('cordova_plugins.js loaded');
        return new Promise((resolve) => {
          document.addEventListener(
            'deviceready',
            () => {
              note('deviceready fired');
              resolve();
            },
            { once: true }
          );
          setTimeout(() => {
            note('deviceready wait timed out (5s)');
            resolve();
          }, 5000);
        });
      })
      .catch((err) => {
        note(`bridge load failed: ${err.message}`);
        throw err;
      });
  }
  return cordovaReady;
};

// Sets up the native push SDK and wires notification taps to in-app navigation.
// Call once on app startup. Wrapped in try/catch: a hiccup in the native
// bridge here must never be allowed to crash the whole app (React has no
// error boundary, so an uncaught error here blanks the entire screen).
export const initPushNotifications = (onNotificationClick) => {
  if (!canUsePush()) return;
  ensureCordova()
    .then(() => initOneSignal(onNotificationClick))
    .catch((err) => console.error('Error loading the push bridge:', err));
};

const initOneSignal = (onNotificationClick) => {
  try {
    const oneSignal = OneSignal();
    if (!oneSignal) {
      console.error('Error initializing push notifications: window.plugins.OneSignal not found');
      return;
    }
    oneSignal.initialize(ONESIGNAL_APP_ID);
    note('OneSignal.initialize called');
    oneSignal.Notifications.requestPermission(true);
    if (onNotificationClick) {
      oneSignal.Notifications.addEventListener('click', (event) => {
        const link = event?.notification?.additionalData?.link;
        if (link) onNotificationClick(link);
      });
    }
  } catch (err) {
    console.error('Error initializing push notifications:', err);
  }
};

// Ties the device's push subscription to our internal user id so a push can
// be targeted at a specific employee. Call on login/logout and on app boot
// if a session is already restored from localStorage.
export const setPushUser = (userId) => {
  if (!canUsePush()) return;
  ensureCordova()
    .then(() => applyPushUser(userId))
    .catch((err) => console.error('Error loading the push bridge:', err));
};

const applyPushUser = (userId) => {
  try {
    const oneSignal = OneSignal();
    if (!oneSignal) {
      console.error('Error setting push user: window.plugins.OneSignal not found');
      return;
    }
    if (userId) oneSignal.login(String(userId));
    else oneSignal.logout();
    note(userId ? `OneSignal.login called (${userId})` : 'OneSignal.logout called');
  } catch (err) {
    console.error('Error setting push user:', err);
  }
};

// Sends an actual OS-level push (shows outside the app, with sound) to a
// specific user via their OneSignal external_id. Fire-and-forget, same
// pattern as notifyUser()'s in-app row insert.
//
// Goes through the send-push-notification Edge Function rather than
// calling OneSignal directly - the REST API key is a server-side secret
// (unlike the App ID) and must never end up in the frontend bundle.
export const sendPushToUser = (userId, { title, body, link }) => {
  if (!userId) return;
  supabase.functions
    .invoke('send-push-notification', { body: { userId, title, body, link } })
    .then(({ error }) => {
      if (error) console.error('Error sending push notification:', error);
    });
};

// For the profile page's diagnostics panel: what the bridge and OneSignal look
// like right now on this device.
const withTimeout = (promise, label) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(`no answer in 4s (${label})`), 4000)),
  ]).catch((err) => `error: ${err?.message || err}`);

export const getPushDiagnostics = async () => {
  const lines = [];
  lines.push(`native app: ${Capacitor.isNativePlatform()}`);
  lines.push(`OneSignal app id configured: ${!!ONESIGNAL_APP_ID}`);
  lines.push(`window.cordova: ${!!window.cordova} / cordova.exec: ${!!(window.cordova && window.cordova.exec)}`);
  lines.push(`window.androidBridge: ${!!window.androidBridge}`);
  const oneSignal = OneSignal();
  lines.push(`window.plugins.OneSignal: ${!!oneSignal}`);
  if (oneSignal) {
    const user = oneSignal.User;
    lines.push(`push subscription id: ${await withTimeout(user.pushSubscription.getIdAsync(), 'id')}`);
    lines.push(`opted in: ${await withTimeout(user.pushSubscription.getOptedInAsync(), 'optedIn')}`);
    lines.push(`external id: ${await withTimeout(user.getExternalId(), 'externalId')}`);
    lines.push(`permission: ${await withTimeout(oneSignal.Notifications.getPermissionAsync(), 'permission')}`);
  }
  lines.push('--- steps taken ---', ...trail);
  return lines;
};

export const isPushAvailable = canUsePush;
